'use client'

import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FaTiktok } from 'react-icons/fa'
import { MdCheckCircle, MdInfoOutline, MdLink, MdLinkOff, MdRefresh, MdWarning } from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    disconnectTikTok,
    getTikTokAdvertisers,
    getTikTokConnection,
    getTikTokOAuthUrl,
    resubscribeTikTokLeads,
    selectTikTokAdvertiser,
    TikTokAdvertisersResult,
    TikTokConnectionSummary,
    updateTikTokPixel,
} from '@/lib/api/marketing/tiktok-ads-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'

const OAUTH_ERRORS: Record<string, string> = {
    missing_code: 'o TikTok não devolveu a autorização',
    invalid_state: 'a autorização expirou, tente de novo',
    token_exchange: 'o TikTok recusou a troca do código',
}

function formatDateTime(value: string | null): string {
    if (!value) return ''
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

/**
 * TikTok Ads da organização: conta de anúncios (OAuth do TikTok for Business), leads dos
 * formulários instantâneos e o Pixel da loja. O envio da venda ao TikTok (Events API) é a
 * próxima fase (docs/tiktok-ads-integration.md).
 */
export default function TikTokAdsIntegrationPage() {
    const { canUpdate, canDelete } = useResourcePermissions('marketing', 'tiktok_ads')
    const [searchParams, setSearchParams] = useSearchParams()
    const [connection, setConnection] = useState<TikTokConnectionSummary | null>(null)
    const [advertisers, setAdvertisers] = useState<TikTokAdvertisersResult | null>(null)
    const [advertiserId, setAdvertiserId] = useState('')
    const [pixelId, setPixelId] = useState('')
    const [loading, setLoading] = useState(true)
    const [connecting, setConnecting] = useState(false)
    const [selecting, setSelecting] = useState(false)
    const [resubscribing, setResubscribing] = useState(false)
    const [savingPixel, setSavingPixel] = useState(false)
    const [showDisconnect, setShowDisconnect] = useState(false)
    const [disconnecting, setDisconnecting] = useState(false)

    const applyConnection = (summary: TikTokConnectionSummary) => {
        setConnection(summary)
        setPixelId(summary.pixelId ?? '')
        setAdvertiserId(summary.advertiserId ?? '')
    }

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const res = await getTikTokConnection()
            if (!res.success) {
                toast.error(res.error || 'Não foi possível carregar o TikTok Ads')
                return
            }
            applyConnection(res.result)
            if (res.result.connected) {
                const list = await getTikTokAdvertisers()
                if (list.success) setAdvertisers(list.result)
            } else {
                setAdvertisers(null)
            }
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    // Retorno do OAuth (?tiktok_connected=1|0)
    useEffect(() => {
        const connected = searchParams.get('tiktok_connected')
        if (connected === '1') {
            toast.success('Conta do TikTok conectada')
        } else if (connected === '0') {
            const error = searchParams.get('error') || ''
            toast.error(`Falha ao conectar o TikTok: ${OAUTH_ERRORS[error] || error || 'erro desconhecido'}`)
        }
        if (connected) {
            searchParams.delete('tiktok_connected')
            searchParams.delete('error')
            setSearchParams(searchParams, { replace: true })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleConnect = async () => {
        setConnecting(true)
        try {
            const res = await getTikTokOAuthUrl()
            if (res.success && res.result?.url) {
                window.location.href = res.result.url
                return
            }
            toast.error(res.error || 'Não foi possível iniciar a conexão com o TikTok')
        } catch {
            toast.error('Erro ao iniciar a conexão com o TikTok')
        }
        setConnecting(false)
    }

    const handleSelectAdvertiser = async () => {
        if (!advertiserId) return
        setSelecting(true)
        try {
            const res = await selectTikTokAdvertiser(advertiserId)
            if (res.success) {
                applyConnection(res.result)
                toast.success(
                    res.result.leadSubscribed
                        ? 'Conta de anúncios salva. Os leads dos formulários passam a chegar.'
                        : 'Conta de anúncios salva, mas o TikTok não liberou os leads. Veja o aviso.'
                )
            } else {
                toast.error(res.error || 'Erro ao salvar a conta de anúncios')
            }
        } finally {
            setSelecting(false)
        }
    }

    const handleResubscribe = async () => {
        setResubscribing(true)
        try {
            const res = await resubscribeTikTokLeads()
            if (res.success) {
                applyConnection(res.result)
                if (res.result.leadSubscribed) toast.success('Leads dos formulários ativados')
                else toast.error(res.result.leadSubscriptionError || 'O TikTok recusou a assinatura dos leads')
            } else {
                toast.error(res.error || 'Erro ao ativar os leads')
            }
        } finally {
            setResubscribing(false)
        }
    }

    const handleDisconnect = async () => {
        setDisconnecting(true)
        try {
            const res = await disconnectTikTok()
            if (res.success) {
                toast.success('Conta do TikTok desconectada')
                setShowDisconnect(false)
                await load()
            } else {
                toast.error(res.error || 'Falha ao desconectar')
            }
        } finally {
            setDisconnecting(false)
        }
    }

    const handleSavePixel = async () => {
        setSavingPixel(true)
        try {
            const res = await updateTikTokPixel(pixelId.trim() || null)
            if (res.success) {
                applyConnection(res.result)
                toast.success(res.result.pixelId ? 'Pixel salvo' : 'Pixel removido')
            } else {
                toast.error(res.error || 'Erro ao salvar o Pixel')
            }
        } finally {
            setSavingPixel(false)
        }
    }

    const isActive = connection?.connected === true
    const pixelUnchanged = (connection?.pixelId ?? '') === pixelId.trim().toUpperCase()
    const advertiserUnchanged = (connection?.advertiserId ?? '') === advertiserId
    const advertiserOptions = advertisers?.ready
        ? advertisers.advertisers
        : (connection?.advertiserIds ?? []).map(id => ({ id, name: null }))

    return (
        <div className="p-4 md:p-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                    <FaTiktok className="text-2xl text-secondary" />
                </div>
                <div>
                    <h1 className="text-xl md:text-2xl font-bold text-base-content">Integração TikTok Ads</h1>
                    <p className="text-sm text-base-content/60">
                        Receba os leads dos formulários do TikTok no funil e meça na loja quem chegou pelos anúncios.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            ) : (
                <div className="space-y-6">
                    {!isActive ? (
                        <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-2xl border border-base-300/50 shadow-sm p-8 md:p-12 text-center">
                            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-secondary/10 flex items-center justify-center">
                                <MdLink className="text-3xl text-secondary/60" />
                            </div>
                            <h3 className="text-lg font-bold text-base-content mb-2">
                                {connection?.status === 'EXPIRED'
                                    ? 'O acesso ao TikTok deixou de valer'
                                    : 'Nenhuma conta de anúncios do TikTok conectada'}
                            </h3>
                            <p className="text-sm text-base-content/60 mb-6 max-w-md mx-auto">
                                Entre com a conta do TikTok for Business que administra os anúncios e autorize a
                                MediarDex. Nenhuma senha fica guardada no sistema.
                            </p>
                            {connection?.lastError && (
                                <p className="text-xs text-error/80 mb-4">Último erro: {connection.lastError}</p>
                            )}
                            <button
                                className="btn btn-secondary gap-2"
                                onClick={handleConnect}
                                disabled={connecting || !canUpdate}
                            >
                                {connecting ? <span className="loading loading-spinner loading-sm" /> : <FaTiktok />}
                                {connection?.status === 'EXPIRED' ? 'Reconectar TikTok' : 'Conectar TikTok'}
                            </button>
                        </div>
                    ) : (
                        <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 space-y-5">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <MdCheckCircle className="text-3xl text-success shrink-0" />
                                    <div>
                                        <p className="font-semibold text-base-content">Conta do TikTok conectada</p>
                                        <p className="text-xs text-base-content/60">
                                            {connection?.connectedAt
                                                ? `Desde ${formatDateTime(connection.connectedAt)}`
                                                : ''}
                                        </p>
                                    </div>
                                </div>
                                <span className="badge badge-success shrink-0">Conectado</span>
                            </div>

                            {advertisers && !advertisers.ready && (
                                <div className="alert alert-warning">
                                    <MdWarning className="text-xl shrink-0" />
                                    <span className="text-sm">{advertisers.reason}</span>
                                </div>
                            )}

                            <div className="form-control w-full md:max-w-md">
                                <label className="label py-1">
                                    <span className="label-text text-sm font-medium">Conta de anúncios</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={advertiserId}
                                    disabled={!canUpdate}
                                    onChange={e => setAdvertiserId(e.target.value)}
                                >
                                    <option value="">Selecione...</option>
                                    {advertiserOptions.map(a => (
                                        <option key={a.id} value={a.id}>
                                            {a.name ? `${a.name} (${a.id})` : a.id}
                                        </option>
                                    ))}
                                </select>
                                <span className="label-text-alt text-base-content/50 mt-1.5">
                                    Os leads dos formulários instantâneos desta conta entram no funil.
                                </span>
                            </div>

                            <div className="flex justify-end">
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={handleSelectAdvertiser}
                                    disabled={selecting || !canUpdate || !advertiserId || advertiserUnchanged}
                                >
                                    {selecting && <span className="loading loading-spinner loading-xs" />}
                                    Salvar conta
                                </button>
                            </div>
                        </div>
                    )}

                    {isActive && connection?.advertiserId && (
                        <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 space-y-4">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h3 className="font-semibold text-base-content">Leads dos formulários</h3>
                                    <p className="text-sm text-base-content/60 mt-1">
                                        Cada lead vira contato e abre a oportunidade no funil do tráfego pago do TikTok
                                        (ou na triagem, se nenhum funil tiver o canal).
                                    </p>
                                </div>
                                {connection.leadSubscribed ? (
                                    <span className="badge badge-success gap-1 shrink-0">
                                        <MdCheckCircle /> Ativo
                                    </span>
                                ) : (
                                    <span className="badge badge-warning shrink-0">Inativo</span>
                                )}
                            </div>

                            {!connection.leadSubscribed && (
                                <div className="alert alert-warning">
                                    <MdWarning className="text-xl shrink-0" />
                                    <div className="text-sm">
                                        <p>
                                            {connection.leadSubscriptionError ||
                                                'Os leads desta conta ainda não estão chegando.'}
                                        </p>
                                        <p className="mt-1 text-base-content/70">
                                            O TikTok só libera os leads quando quem conectou é Administrador da conta de
                                            anúncios.
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <p className="text-sm text-base-content/70">
                                    {connection.lastLeadAt
                                        ? `Último lead recebido em ${formatDateTime(connection.lastLeadAt)}`
                                        : 'Nenhum lead recebido ainda.'}
                                </p>
                                {!connection.leadSubscribed && (
                                    <button
                                        className="btn btn-outline btn-sm"
                                        onClick={handleResubscribe}
                                        disabled={resubscribing || !canUpdate}
                                    >
                                        {resubscribing && <span className="loading loading-spinner loading-xs" />}
                                        Tentar ativar de novo
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 space-y-5">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h3 className="font-semibold text-base-content">TikTok Pixel da loja</h3>
                                <p className="text-sm text-base-content/60 mt-1">
                                    Carregado em todas as páginas da loja online, para o TikTok medir visitas e
                                    otimizar os anúncios. Não depende da conta conectada.
                                </p>
                            </div>
                            {connection?.pixelId && (
                                <span className="badge badge-success gap-1 shrink-0">
                                    <MdCheckCircle /> Ativo
                                </span>
                            )}
                        </div>

                        <div className="form-control w-full md:max-w-md">
                            <label className="label py-1">
                                <span className="label-text text-sm font-medium">ID do Pixel</span>
                            </label>
                            <input
                                className="input input-bordered w-full font-mono"
                                placeholder="Ex.: CABCD1234EFGH5678IJ"
                                value={pixelId}
                                disabled={!canUpdate}
                                onChange={e => setPixelId(e.target.value)}
                            />
                            <span className="label-text-alt text-base-content/50 mt-1.5">
                                No TikTok Ads Manager: Ferramentas, Gerenciador de Eventos, o código abaixo do nome
                                do Pixel. Deixe vazio para parar de carregar.
                            </span>
                        </div>

                        <div className="flex justify-end">
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={handleSavePixel}
                                disabled={savingPixel || !canUpdate || pixelUnchanged}
                            >
                                {savingPixel && <span className="loading loading-spinner loading-xs" />}
                                Salvar Pixel
                            </button>
                        </div>
                    </div>

                    <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 space-y-3">
                        <h3 className="font-semibold text-base-content">Anúncio que leva ao WhatsApp</h3>
                        <p className="text-sm text-base-content/60">
                            O TikTok não informa de qual anúncio veio uma conversa do WhatsApp. Em Marketing,
                            Campanhas, crie uma campanha com destino WhatsApp e adicione um link com a origem
                            "Anúncio do TikTok". Use esse link como destino do anúncio (local de otimização
                            "Website").
                        </p>
                        <p className="text-sm text-base-content/60">
                            A conversa fica ligada à campanha e a oportunidade abre no funil do canal TikTok, mesmo
                            quando o TikTok não acrescenta o identificador do clique.
                        </p>
                    </div>

                    <div className="alert">
                        <MdInfoOutline className="text-xl shrink-0" />
                        <span className="text-sm">
                            Enviar as vendas de volta ao TikTok, para ele otimizar os anúncios por quem compra, é a
                            próxima etapa da integração.
                        </span>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                        <button className="btn btn-ghost btn-sm gap-1" onClick={load}>
                            <MdRefresh className="text-lg" /> Atualizar
                        </button>
                        {isActive && canDelete && (
                            <button
                                className="btn btn-ghost btn-sm gap-1 text-error"
                                onClick={() => setShowDisconnect(true)}
                            >
                                <MdLinkOff className="text-lg" /> Desconectar
                            </button>
                        )}
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDisconnect}
                onClose={() => setShowDisconnect(false)}
                onConfirm={handleDisconnect}
                title="Desconectar o TikTok"
                message="Os leads dos formulários do TikTok param de chegar e a autorização é cancelada no TikTok. O Pixel da loja continua. Deseja continuar?"
                confirmButtonText="Desconectar"
                cancelButtonText="Cancelar"
                loading={disconnecting}
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
